import 'react-dates/initialize';

import moment from 'moment-timezone';
import React from 'react';
import { DateRangePicker } from 'react-dates';

import { isWithinMediaSizeOrBelow } from '~/src/dash/publicaUI';
import { BaseContextState, IBaseState } from '~/src/dash/views/base/contexts';

interface IProps {
  startDate: moment.Moment;
  endDate: moment.Moment;
  calStartDate: moment.Moment;
  calEndDate: moment.Moment;
  startDateID: string;
  endDateID: string;
  calendarInfoPosition?: 'before' | 'after' | 'top' | 'bottom';
  focusedInput: 'startDate' | 'endDate' | null;
  isCompareCalendar: boolean;
  handleCalendarDateChange: (start: moment.Moment, end: moment.Moment, isCustom: boolean) => void;
  handleFocusedInputChange: (focusedInput: IFocusedInput) => void;
  isDayHighlighted: (day: moment.Moment) => boolean;
  stateDateWrapper: (date: moment.Moment) => moment.Moment;
  renderCalendarInfo: () => JSX.Element;
  dateFormat: string;
  timezone: string;
  trackOnClose?: (final: {
    startDate: moment.Moment | null;
    endDate: moment.Moment | null;
  }) => void;
  excludeToday?: boolean;
  anchorDirection?: 'left' | 'right' | undefined;
  minDate?: moment.Moment;
  numberOfMonths?: number;
  withPortal?: boolean;
}

interface IState {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

export type IFocusedInput = 'endDate' | 'startDate' | null;

export class CalendarWrapper extends React.Component<IProps, IState> {
  static contextType = BaseContextState;

  constructor(props: IProps) {
    super(props);
    this.state = {
      startDate: props.startDate,
      endDate: props.endDate,
    };
    this.onDatesChange = this.onDatesChange.bind(this);
  }

  componentDidUpdate(prevProps: IProps) {
    if (prevProps.startDate !== this.props.startDate || prevProps.endDate !== this.props.endDate) {
      const { stateDateWrapper } = this.props;
      this.setState({
        startDate: this.props.startDate && stateDateWrapper(this.props.startDate),
        endDate: this.props.endDate && stateDateWrapper(this.props.endDate),
      });
    }
  }

  onDatesChange({ startDate, endDate }: { startDate: moment.Moment; endDate: moment.Moment }) {
    const { stateDateWrapper } = this.props;
    // only change date if its truthy
    this.setState(
      {
        startDate:
          startDate && stateDateWrapper(startDate)
            ? startDate && stateDateWrapper(startDate)
            : this.state.startDate,
        endDate:
          endDate && stateDateWrapper(endDate)
            ? endDate && stateDateWrapper(endDate)
            : this.state.endDate,
      },
      () => {
        this.props.handleCalendarDateChange(this.state.startDate, this.state.endDate, true);
      },
    );
  }

  render() {
    const {
      calStartDate,
      calEndDate,
      startDateID,
      endDateID,
      calendarInfoPosition,
      focusedInput,
      handleFocusedInputChange,
      isDayHighlighted,
      renderCalendarInfo,
      dateFormat,
      timezone,
      trackOnClose,
      excludeToday,
      anchorDirection,
      numberOfMonths,
      withPortal = true,
    } = this.props;

    const propsToSend = {
      startDate: this.props.startDate,
      endDate: this.props.endDate,
      calendarInfoPosition: this.props.calendarInfoPosition,
      focusedInput: this.props.focusedInput,
      isDayHighlighted: this.props.isDayHighlighted,
      renderCalendarInfo: this.props.renderCalendarInfo,
    };

    // since we are in a class component, we must pass in baseState via this.context.
    // this causes 'base' to be unknown so we ignore
    // @ts-ignore
    const base: IBaseState = this.context;
    const isLargeMediaSizeOrBelow = isWithinMediaSizeOrBelow('large', base.mediaSize);
    const numMonths = numberOfMonths || isLargeMediaSizeOrBelow ? 1 : 2;

    return (
      <DateRangePicker
        {...propsToSend}
        small={true}
        isOutsideRange={(day: moment.Moment) => {
          if (excludeToday || this.props.minDate) {
            const isToday =
              day.day() === moment().tz(timezone).day() &&
              day.week() === moment().tz(timezone).week() &&
              day.month() === moment().tz(timezone).month() &&
              day.year() === moment().tz(timezone).year();
            const isBeforeMinDate = this.props.minDate ? day.isBefore(this.props.minDate) : false;
            return (
              !isInclusivelyBeforeDay(day, moment().tz(timezone)) || isToday || isBeforeMinDate
            );
          }
          return !isInclusivelyBeforeDay(day, moment().tz(timezone));
        }}
        onClose={trackOnClose}
        calendarInfoPosition={calendarInfoPosition}
        anchorDirection={anchorDirection || 'right'}
        onDatesChange={this.onDatesChange}
        onFocusChange={handleFocusedInputChange}
        focusedInput={focusedInput}
        isDayHighlighted={isDayHighlighted}
        renderCalendarInfo={renderCalendarInfo}
        startDate={calStartDate}
        startDateId={startDateID}
        endDate={calEndDate}
        endDateId={endDateID}
        keepOpenOnDateSelect={true}
        orientation='horizontal'
        daySize={40}
        hideKeyboardShortcutsPanel={true}
        initialVisibleMonth={() =>
          moment()
            .tz(timezone)
            .subtract(numMonths === 1 ? 0 : 1, 'months')
        }
        displayFormat={dateFormat}
        minimumNights={0}
        numberOfMonths={numMonths || 2}
        withPortal={withPortal}
      />
    );
  }
}

const isBeforeDay = (a: moment.Moment, b: moment.Moment): boolean => {
  if (!moment.isMoment(a) || !moment.isMoment(b)) {
    return false;
  }

  const aYear = a.year();
  const aMonth = a.month();

  const bYear = b.year();
  const bMonth = b.month();

  const isSameYear = aYear === bYear;
  const isSameMonth = aMonth === bMonth;

  if (isSameYear && isSameMonth) {
    return a.date() <= b.date();
  }
  if (isSameYear) {
    return aMonth <= bMonth;
  }
  return aYear <= bYear;
};

const isInclusivelyBeforeDay = (a: moment.Moment, b: moment.Moment): boolean => {
  if (!moment.isMoment(a) || !moment.isMoment(b)) {
    return false;
  }
  return isBeforeDay(a, b);
};
